<template>
  <section class="ma-4 ma-md-8">
    <t-floating-bar v-if="eventTitle">
      {{ eventTitle }}
    </t-floating-bar>
    <t-page-title
      title="Vendas | Relatório de Portadores"
      :on-report="() => datatable?.openDialogReport()"
    />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="datatable"
          :report="report"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
          :pagination="pagination"
        >
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split("-")[0] }}</span>
          </template>
          <template #[`item.name`]="{ item }">
            <span> {{ (!!item.name ? item.name : item.order?.owner?.name)?.toLocaleUpperCase() }}</span>
          </template>
          <template #[`item.document`]="{ item }">
            <span>{{ formatDoc(!!item.document ? item.document : item.order?.owner?.document, !!item.document ? item.document_type : item.order?.owner?.document_type) }}</span>
          </template>
          <template #[`item.email`]="{ item }">
            <span>{{ item.email ?? item.order?.owner?.email }}</span>
          </template>
          <template #[`item.value`]="{ item }">
            <span>{{
              new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(item.value)
            }}</span>
          </template>
          <template #[`item.created_at`]="{ item }">
            <span>
              {{
                new Date(item.created_at).toLocaleDateString("pt-BR", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                })
              }}</span>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref, type Ref, useTemplateRef } from 'vue'
import type { AxiosResponse } from 'axios'
import TPageTitle from '@/components/TPageTitle.vue'
import TFloatingBar from '@/components/TFloatingBar.vue'
import type {
  DataTableFilter,
  DataTableHeader,
  DataTablePagination,
} from '@/types/DataTable'
import type { Report, ReportItem } from '@/types/Report'
import type PaginatedResponse from '@/types/PaginatedResponse'
import type OrderItem from '@/types/OrderItem'
import DataTable, { type DataTableType } from '@/components/datatable/DataTable.vue'
import OrderService from '@/services/OrderService'
import { formatDocument } from '@/helpers/formatters/document'
import { useEventFilter } from '@/helpers/reports/eventFilter'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useSectorFilter } from '@/helpers/reports/sectorFilter'
import { useTicketTypeFilter } from '@/helpers/reports/ticketTypeFilter'
import { useProducerStore } from '@/stores/producer'
import { useSnackBar } from '@/stores/snackbar'

const snackbar = useSnackBar()

const datatable = useTemplateRef<DataTableType>('datatable')

const resource = 'reports'

const headers: DataTableHeader[] = [
  {
    title: 'ID',
    value: 'id',
    key: 'id',
    sortable: false,
  },
  {
    title: 'Nome do Portador',
    value: 'name',
    key: 'name',
    sortable: false,
  },
  {
    title: 'Documento do Portador',
    value: 'document',
    key: 'document',
    sortable: false,
    align: 'center',
  },
  {
    title: 'Email',
    value: 'email',
    key: 'email',
    sortable: false,
  },
  {
    title: 'Tipo de ingresso',
    value: 'type',
    key: 'type',
    sortable: false,
  },
  {
    title: 'Setor',
    value: 'sector',
    key: 'sector',
    sortable: false,
  },
  {
    title: 'Situação da compra',
    value: 'order.status',
    key: 'status',
    sortable: false,
  },
  {
    title: 'Valor do Ingresso',
    value: 'value',
    key: 'value',
    sortable: false,
  },
  {
    title: 'Data de Criação',
    value: 'created_at',
    key: 'created_at',
    sortable: false,
    align: 'center',
  },
]

const pagination: DataTablePagination = {
  page: 1,
  itemsPerPage: 100,
  total: 0,
  totalPages: 0,
}

const report: Ref<Report> = ref({
  items: [
    { label: 'Código', field: 'id', active: false, mandatory: false },
    { label: 'Número do pedido', field: 'order.order_number', mandatory: false },
    { label: 'Data de criação', field: 'created_at', active: false },
    { label: 'Setor', field: 'sector', active: false, mandatory: false },
    { label: 'Tipo de ingresso', field: 'type', active: false },
    { label: 'Lote', field: 'batch.number', active: false },
    { label: 'Valor do Ingresso', field: 'value', active: false },
    { label: 'Forma de pagamento', field: 'order.payment_method.name', active: false },
    { label: 'Cupons utilizados', field: 'order.coupon_codes', active: false },
    { label: 'Hora de Leitura', field: 'passport.reading.created_at', active: false },
    { label: 'Nome do portador', field: 'name', active: true },
    { label: 'Documento do portador', field: 'document', active: true },
    { label: 'Tipo de documento do portador', field: 'document_type', active: false },
    { label: 'Email do portador', field: 'email', active: true },
    { label: 'Telefone do portador', field: 'cellphone', active: true },
    { label: 'Nome do comprador', field: 'order.owner.name', active: false },
    { label: 'Documento do comprador', field: 'order.owner.document', active: false },
    { label: 'Data de nascimento do comprador', field: 'order.owner.birth_date', active: false },
    { label: 'Email do comprador', field: 'order.owner.email', active: false },
    { label: 'Celular do comprador', field: 'order.owner.cellphone', active: false },
    { label: 'Campos adicionais', field: 'additional_fields', active: false },
  ] as ReportItem[],
  filename: 'relatorio_portadores',
  provider: async (filters: string, selected) => {
    return await new OrderService().report(filters, selected)
  },
})

const eventFilter = useEventFilter({
  producer: useProducerStore().producer!,
  label: 'Evento',
  field: 'event_id',
  type: 'select',
})

// order_status uses English enum keys sent to the backend (Confirmed,
// Courtesy, etc.), distinct from usePaymentStatusFilter's Portuguese
// labels-as-values - constructed directly to match the original exactly,
// including the default pre-selected value.
const orderStatusModel: Ref<string[] | null> = ref(['Confirmed', 'Courtesy'])

const orderStatusFilter: DataTableFilter<string[]> = {
  model: orderStatusModel,
  value: computed(() => orderStatusModel.value?.join(',') ?? null),
  items: computed(() => [
    { title: 'Todos', value: '' },
    { title: 'Cancelado', value: 'Canceled' },
    { title: 'Confirmado', value: 'Confirmed' },
    { title: 'Cortesia', value: 'Courtesy' },
    { title: 'Recusado', value: 'Denied' },
    { title: 'Aguardando Pagamento', value: 'Waiting' },
    { title: 'Processando Pagamento', value: 'Processing' },
  ]),
  label: 'Situação da compra',
  field: 'order_status',
  type: 'select',
  multiple: true,
  loading: ref(false),
  ready: Promise.resolve(true),
  required: false,
}

const filters: Ref<DataTableFilter[]> = ref([
  eventFilter,
  useTextFilter({ label: 'Nome do portador', field: 'holder.name', icon: 'fa-solid fa-signature', required: false }),
  useTextFilter({ label: 'Documento', field: 'holder.document', icon: 'fa-solid fa-id-card', required: false }),
  useTextFilter({ label: 'Email', field: 'holder.email', icon: 'fa-solid fa-at', required: false }),
  orderStatusFilter,
  useTicketTypeFilter({ event: eventFilter.model, label: 'Tipo de Ingresso', field: 'type_id' }),
  useSectorFilter({ event: eventFilter.model, label: 'Setor', field: 'sector_id' }),
])

const eventTitle = computed(() => {
  const found = eventFilter.items?.value.find(i => i.value === eventFilter.model.value)
  return found?.value ? found.title : null
})

async function fetch(page: number, filtersString: string, per_page?: number): Promise<AxiosResponse<PaginatedResponse<OrderItem>>> {
  try {
    return await new OrderService().items(
      page,
      filtersString,
      per_page,
      eventFilter.model.value as string,
    ) as unknown as AxiosResponse<PaginatedResponse<OrderItem>>
  } catch (error) {
    snackbar.show('Não foi possível buscar as leituras', 'error')

    throw error
  }
}

function formatDoc(document: string | undefined, documentType?: 'cpf' | 'passport') {
  return formatDocument(document, documentType)
}
</script>
